import type React from "react"
import { Children, cloneElement } from "react"

import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

type DraggableProps = {
  id: string
  children: React.ReactNode
  placeholder?: boolean
  disabled?: boolean
  placeholderOpacity?: number
  style?: React.CSSProperties
  attachToChild?: boolean
}

export const Draggable = ({
  id,
  children,
  placeholder,
  disabled,
  placeholderOpacity = 0.2,
  style,
  attachToChild,
}: DraggableProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, active } = useSortable({
    id,
    disabled: placeholder || disabled,
    data: {
      overlay: children,
    },
  })

  const isDragging = !!active?.id && active?.id === id

  const transformStyle = {
    ...(style || {}),
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? placeholderOpacity : 1, // Hide the original element when dragging, if overlay is visible
    cursor: placeholder || disabled ? "default" : "grab",
    pointerEvents: isDragging ? "none" : "auto",
  }

  if (attachToChild) {
    const child = Children.only(children)
    return cloneElement(child as React.ReactElement, {
      ref: (ref) => {
        setNodeRef(ref)
      },
      style: { ...child.props.style, ...transformStyle },
      ...attributes,
      ...listeners,
    })
  }

  return (
    <div ref={setNodeRef} style={transformStyle} {...attributes} {...listeners}>
      {children}
    </div>
  )
}
